import { ArrowLeft, ImageIcon, Merge, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { StatusBadge, btnGhost, btnSecondary } from '../components/ui';
import { ApiError, type DuplicatePair, type DuplicateSide } from '../lib/api';
import { categoryLabel, formatRange, formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

const score = (n: number) => n.toFixed(2).replace('.', ',');

/**
 * Doppioni tra gli eventi in programma (docs/07-admin-e-bot.md, "Doppioni"): coppie dello stesso
 * comune con date vicine e titolo simile. Da qui si uniscono, o si segnano come eventi diversi
 * (la coppia non torna più).
 */
export function Duplicates() {
  const { api, canEdit } = useSession();
  const [pairs, setPairs] = useState<DuplicatePair[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!canEdit) return;
    api<{ pairs: DuplicatePair[] }>('/events/duplicates')
      .then((r) => setPairs(r.pairs))
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e)),
      );
  }, [api, canEdit]);

  const ignore = async (p: DuplicatePair) => {
    const key = `${p.a.id}#${p.b.id}`;
    setBusy(key);
    try {
      await api('/events/duplicates/ignore', { method: 'POST', body: { a: p.a.id, b: p.b.id } });
      setPairs((list) => list?.filter((x) => `${x.a.id}#${x.b.id}` !== key) ?? null);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  };

  // resta di solito il più completo: con l'immagine, poi il più vecchio (indirizzo più noto)
  const order = (p: DuplicatePair): [DuplicateSide, DuplicateSide] =>
    p.b.hasImage && !p.a.hasImage ? [p.b, p.a] : [p.a, p.b];

  return (
    <main className="mx-auto max-w-[1400px] p-4 md:p-6">
      <Link to="/events" className={btnGhost}>
        <ArrowLeft className="size-4" aria-hidden /> Eventi
      </Link>
      <h1 className="mt-2 font-display text-h2">Possibili doppioni</h1>
      <p className="mt-1 max-w-3xl text-sm text-muted">
        Eventi in programma nello stesso comune, con date che si sovrappongono e titolo simile.
        Unendoli resta un solo evento: l’indirizzo dell’altro porta a quello che resta. Se sono
        eventi diversi, segnali e la coppia non viene più proposta.
      </p>

      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {!pairs && !error && <p className="mt-6 text-sm text-muted">Ricerca dei doppioni…</p>}
      {pairs && pairs.length === 0 && (
        <p className="mt-6 text-sm">Nessun possibile doppione tra gli eventi in programma.</p>
      )}

      {pairs && pairs.length > 0 && (
        <ul className="mt-4 flex flex-col gap-3">
          {pairs.map((p) => {
            const [a, b] = order(p);
            const key = `${p.a.id}#${p.b.id}`;
            return (
              <li key={key} className="border border-surface-strong">
                <div className="grid md:grid-cols-2">
                  {[a, b].map((s, i) => (
                    <div
                      key={s.id}
                      className={`p-3 ${i === 1 ? 'border-t border-surface-strong md:border-t-0 md:border-l' : ''}`}
                    >
                      <Link
                        to={`/events/${s.id}`}
                        className="font-medium hover:underline"
                        target="_blank"
                      >
                        {s.title}
                      </Link>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
                        <StatusBadge status={s.status} />
                        <span>{formatRange(s.startDate, s.endDate)}</span>
                        <span>{[s.frazione, s.comune].filter(Boolean).join(', ')}</span>
                        <span>{categoryLabel(s.category)}</span>
                        {s.hasImage && <ImageIcon className="size-3.5" aria-label="Con immagine" />}
                        <span>aggiornato {formatStamp(s.updatedAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 border-t border-surface-strong bg-surface px-3 py-2">
                  <span className="text-xs text-muted">
                    Somiglianza {score(p.score)} · titolo {score(p.titleScore)}
                  </span>
                  <div className="ml-auto flex gap-2">
                    <button
                      onClick={() => void ignore(p)}
                      disabled={busy === key}
                      className={btnGhost}
                      title="Sono eventi diversi: non proporre più questa coppia"
                    >
                      <X className="size-4" aria-hidden /> Non sono doppioni
                    </button>
                    <Link
                      to={`/events/merge?target=${a.id}&source=${b.id}`}
                      className={btnSecondary}
                    >
                      <Merge className="size-4" aria-hidden /> Unisci…
                    </Link>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
